import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import SignIn from "./components/SignIn";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./components/Home";
import { useEffect, useRef } from "react";
import ConfigService from "@/components/service/ConfigService.js";
import AuthService from "@/components/service/AuthService.js";
import Error from "@/components/Error.jsx";
import { useDispatch } from "react-redux";
import { set_principal } from "@/components/_redux/authentication/authenticationSlice.js";
import ForgotPassword from "@/components/ForgotPassword.jsx";
import ChangePassword from "@/components/ChangePassword.jsx";
import SavePassword from "@/components/SavePassword.jsx";
import SignUp from "@/components/SignUp.jsx";
import OttSent from "@/components/OttSent.jsx";
import OttRequest from "@/components/OttRequest.jsx";

function Layout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="600:mt-[90px] mt-[60px] flex flex-1 flex-col">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Routes whose component reads the signed-in principal from the store.
// Change-password lands here with a session the reset token just signed in,
// and /me is how the SPA learns whether that worked; forgot-password only
// uses it to prefill the email. Every other route (sign-in above all) is for
// a signed-out visitor, where /me is a guaranteed 401 — so it isn't asked.
const PRINCIPAL_ROUTES = ["/change-password", "/forgot-password"];

const UNAUTHENTICATED = { id: "unauthenticated" };

function App() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const configService = ConfigService.getInstance();
  const authService = AuthService.getInstance();

  const wantsPrincipal = PRINCIPAL_ROUTES.some((route) =>
    pathname.startsWith(configService.formulateContextPath(route)),
  );

  // Nothing here gates render: the layout and the matched route mount on the
  // first pass, and the principal (when a route needs it) arrives in the
  // background via the store. The ref guards StrictMode's double-run of
  // effects in dev, which would otherwise fire the requests twice.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !wantsPrincipal) return;
    started.current = true;

    configService
      .load()
      .then(() => {
        authService.updateConfig(configService._data);
        return authService.retrieveUserInfoFromServer();
      })
      .then((principal) => dispatch(set_principal(principal)))
      .catch((error) => {
        console.log(error);
        dispatch(set_principal(UNAUTHENTICATED));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsPrincipal]);

  return (
    <Layout>
      <Routes>
        <Route path={configService.formulateContextPath("/login")} element={<SignIn />} />
        <Route path={configService.formulateContextPath("/register")} element={<SignUp />} />
        <Route path={configService.formulateContextPath("/error")} element={<Error />} />
        <Route path={configService.formulateContextPath("/forgot-password")} element={<ForgotPassword />} />
        <Route path={configService.formulateContextPath("/change-password/:email/:token")} element={<ChangePassword />} />
        <Route path={configService.formulateContextPath("/save-password")} element={<SavePassword/>} />


        {
          // One Time Use Token Flow
        }
        <Route path={configService.formulateContextPath("/ott/request")} element={<OttRequest/>} />
        <Route path={configService.formulateContextPath("/ott/sent")} element={<OttSent/>} />

        <Route path={configService.formulateContextPath("/")} element={<Home/>} />
        <Route path="*" element={<Navigate to={configService.formulateContextPath("/")} replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
